import { ToasterService } from '@abp/ng.theme.shared';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IdentityLinkLoginService } from '@volo/abp.ng.identity/config';
import { from, of } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import { tokenStorage } from './token-storage';

/**
 * Replaces ABP's LinkLoginHandler, which IdentityConfigModule instantiates eagerly on every page
 * load. Two problems with the original:
 *
 * 1. It honoured a "?handler=linkLogin&token={...}" query parameter by writing every key in that
 *    JSON straight into localStorage, with nothing authenticating the caller. Anyone who could get
 *    a user to open a link could plant an access token - session fixation - or overwrite unrelated
 *    keys such as abpSession, which carries the selected tenant.
 * 2. Its own cross-tenant-domain redirect put the access and refresh token in a query string,
 *    where they end up in server logs, proxy logs and browser history.
 *
 * This version refuses the query-parameter token entirely and keeps tokens in tokenStorage with
 * everything else. The cost is that linking to an account on a different tenant domain no longer
 * completes; it cannot be made safe from the client, because correlating the redirect with the
 * request that started it would need state shared across two origins, and neither Web Storage nor
 * a host-only cookie is shared across them. Same-domain linked login is unaffected.
 */
@Injectable()
export class SitePlantLinkLoginHandler {
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);
	private readonly identityLinkLoginService = inject(IdentityLinkLoginService);
	private readonly toaster = inject(ToasterService);

	constructor() {
		this.listenToQueryParams();
	}

	private listenToQueryParams() {
		this.route.queryParams
			.pipe(
				filter(params => params.handler === 'linkLogin' && (params.linkUserId || params.token)),
				switchMap(params => {
					if (params.token) {
						console.warn(
							'Ignoring a linkLogin token supplied in the URL. Tokens are never accepted from ' +
								'query parameters.'
						);
						this.toaster.error('AbpAccount::DefaultErrorMessage');
						return this.scrubQueryParams();
					}

					return this.linkLogin({
						linkUserId: params.linkUserId,
						linkTenantId: params.linkTenantId,
					});
				})
			)
			.subscribe();
	}

	private linkLogin(input: { linkUserId: string; linkTenantId?: string }) {
		return this.identityLinkLoginService.linkLogin(input).pipe(
			catchError((err: HttpErrorResponse) => {
				this.toaster.error(err.error?.error);
				return of(null);
			}),
			switchMap(res => {
				if (!res) {
					return of(null);
				}

				if ((res as { tenant_domain?: string }).tenant_domain) {
					this.toaster.error('AbpAccount::DefaultErrorMessage');
					return this.scrubQueryParams();
				}

				const now = new Date().valueOf();
				tokenStorage.setItem('access_token', res.access_token);
				tokenStorage.setItem('access_token_stored_at', `${now}`);
				tokenStorage.setItem('expires_at', `${now + res.expires_in * 1000}`);
				if (res.refresh_token) {
					tokenStorage.setItem('refresh_token', res.refresh_token);
				}

				return this.scrubQueryParams().pipe(tap(() => location.reload()));
			})
		);
	}

	private scrubQueryParams() {
		return from(
			this.router.navigate(['.'], {
				relativeTo: this.route,
				queryParams: { handler: null, linkUserId: null, linkTenantId: null, token: null },
				queryParamsHandling: 'merge',
			})
		);
	}
}
